import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { SearchOutlined } from '@ant-design/icons';
import { useStores } from '@/hooks/use-stores';
import * as S from './ConversationHistory.styles';
import AISparkleIcon from '@/comp/common/icons/AISparkleIcon';
import { theme } from 'antd';

const { useToken } = theme;

const ConversationHistory: React.FC = observer(() => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { chatbotStore } = useStores();

  const handleNewConversation = () => {
    chatbotStore.createNewConversation();
  };

  const handleConversationClick = (conversationId: string) => {
    chatbotStore.currentConversationId = conversationId;
  };

  // Group conversations by time period and sort by latest message time (most recent first)
  const getLast7DaysConversations = () => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return chatbotStore.conversations
      .filter(conv => conv.updatedAt >= sevenDaysAgo)
      .sort((a, b) => {
        // Sort by updatedAt in descending order (most recent first)
        return b.updatedAt.getTime() - a.updatedAt.getTime();
      });
  };

  const last7DaysConversations = getLast7DaysConversations();

  return (
    <S.Container>
      <S.Content>
        <S.Section $hasDivider={last7DaysConversations.length > 0}>
          <S.List>
            <S.ConversationItem $isActive={false} onClick={handleNewConversation}>
              <AISparkleIcon width={12} height={12} color={token.colorPrimary} />
              <S.ConversationTitle $isPrimary>{t('NewConversation')}</S.ConversationTitle>
            </S.ConversationItem>

            <S.ConversationItem $isActive={false}>
              <SearchOutlined style={{ fontSize: '16px' }} />
              <S.ConversationTitle>{t('Search')}</S.ConversationTitle>
            </S.ConversationItem>
          </S.List>
        </S.Section>

        {last7DaysConversations.length > 0 && (
          <S.Section>
            <S.SectionLabel>{t('Last7Days')}</S.SectionLabel>
            <S.List>
              {last7DaysConversations.map(conversation => {
                const firstMessage = conversation.messages.find(msg => msg.role === 'user');
                const preview = firstMessage?.content || t('NewConversation');
                const isActive = conversation.id === chatbotStore.currentConversationId;

                return (
                  <S.ConversationItem
                    key={conversation.id}
                    $isActive={isActive}
                    onClick={() => handleConversationClick(conversation.id)}
                  >
                    <S.ConversationTitle>{preview}</S.ConversationTitle>
                  </S.ConversationItem>
                );
              })}
            </S.List>
          </S.Section>
        )}
      </S.Content>
    </S.Container>
  );
});

ConversationHistory.displayName = 'ConversationHistory';

export default ConversationHistory;
